import { inject, Injectable, Injector, makeEnvironmentProviders, provideAppInitializer, runInInjectionContext } from "@angular/core";
import { JsonPointer } from "@noah-ark/json-patch";
import { firstValueFrom, ReplaySubject } from "rxjs";

export type AppConfig = Record<string, unknown>;

// @Injectable({ providedIn: "root" })
export class ConfigService {
    private _config = null as AppConfig;
    private readonly _loaded = new ReplaySubject<AppConfig | null>(1);

    constructor(loader: () => Promise<AppConfig> = () => Promise.resolve({})) {
        this.load(loader);
    }
    private async load(loader: () => Promise<AppConfig>) {
        try {
            const config = await loader();
            this._config = config;
            this._loaded.next(config);
            this._loaded.complete();
        } catch (error) {
            console.error("Failed to load config:", error);
            this._loaded.error(error);
        }
    }

    config() {
        return firstValueFrom(this._loaded.asObservable());
    }

    /**
     * Retrieves a value from the loaded configuration using a JSON Pointer path.
     * @param key The JSON Pointer path (e.g., 'apiUrl', '/features/myFeature').
     * @returns The value, or null if not found or config not loaded yet.
     */
    async get<T = any>(key: string): Promise<T | null> {
        await firstValueFrom(this._loaded.asObservable());
        return this.getSync<T>(key);
    }

    getSync<T = any>(key: string): T | null {
        const c = this._config;
        if (!c) {
            console.warn(`ConfigService: Attempted to read config for key '${key}' before configuration was loaded.`);
            return null;
        }
        return JsonPointer.get<T>(c, key) ?? null;
    }
}

export function provideConfig(loadFn: () => Promise<AppConfig>) {
    return makeEnvironmentProviders([{ provide: ConfigService, useFactory: () => new ConfigService(loadFn), multi: false }]);
}
